/** Documents — edit form (RHF + zod + shadcn Form via Refine useForm). */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CONFIDENTIALITY,
  updateDocumentSchema,
  type Document,
  type UpdateDocumentInput,
} from "@aos/shared";

import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { PageHeader, LoadingState, ErrorState } from "../projects/_shared";
import {
  FormGrid,
  MarkdownField,
  SelectField,
  TextField,
} from "../projects/_form";

export function DocumentEdit() {
  const form = useForm<Document, any, UpdateDocumentInput>({
    resolver: zodResolver(updateDocumentSchema),
    refineCoreProps: { resource: "documents", action: "edit" },
  });

  const {
    refineCore: { formLoading, query },
    saveButtonProps,
    control,
  } = form;

  if (query?.isLoading) return <LoadingState label="Loading document…" />;
  if (query?.isError) return <ErrorState error={query.error} />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Edit Document"
        description="Update this document's details and summary."
        icon="documents"
      />
      <Form {...form}>
        <form onSubmit={saveButtonProps.onClick}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Document details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <TextField control={control} name="title" label="Title" />
              <FormGrid>
                <TextField
                  control={control}
                  name="source_system"
                  label="Source system"
                />
                <TextField
                  control={control}
                  name="document_type"
                  label="Document type"
                />
                <TextField
                  control={control}
                  name="external_url"
                  label="External URL"
                />
                <SelectField
                  control={control}
                  name="confidentiality"
                  label="Confidentiality"
                  options={CONFIDENTIALITY}
                />
                <TextField
                  control={control}
                  name="project_id"
                  label="Project ID"
                />
                <TextField
                  control={control}
                  name="owner_user_id"
                  label="Owner User ID"
                />
              </FormGrid>
              <MarkdownField
                control={control}
                name="agent_summary"
                label="Agent summary"
                description="Agent-readable markdown summary of the document."
              />
            </CardContent>
            <CardFooter className="justify-end">
              <Button type="submit" disabled={formLoading}>
                {formLoading ? "Saving…" : "Save Changes"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}

export default DocumentEdit;
